import React, { useEffect, useState } from 'react'
import { formatCurrency } from '../../../../lib/utils/formatNumber'
import {
  MaskedProperty,
  Property,
  PropertyWithAccess,
} from '../../../../types/Property/Property'
import { useTranslation } from 'next-i18next'
import Button, { buttonHierarchy, buttonShape } from '../../../Button'
import useCurrency from '../../../../hooks/useCurrency'
import useLocale from '../../../../hooks/useLocale'

type FractionInputProps = {
  property: PropertyWithAccess | Property | MaskedProperty
  initialValue: number
  onValueChange: (fractionAmount: number) => void
  minFractionAmount: number
}

export default function FractionInput({
  property,
  initialValue,
  onValueChange,
  minFractionAmount,
}: FractionInputProps) {
  const { t } = useTranslation('originals/global')

  const fractionAvailable: number = property.fractionAvailable

  const [fractionAmount, setFractionAmount] = useState<number>(initialValue)
  const { currency } = useCurrency()
  const { locale } = useLocale()

  useEffect(() => {
    setFractionAmount(initialValue)
  }, [initialValue])

  const handleIncrement = () => {
    setFractionAmount(fractionAmount + 1)
    onValueChange(fractionAmount + 1)
  }

  const handleDecrement = () => {
    if (fractionAmount > minFractionAmount) {
      setFractionAmount(fractionAmount - 1)
      onValueChange(fractionAmount - 1)
    }
  }

  const onFractionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue: number = parseInt(e.target.value)
    if (!isNaN(inputValue) && inputValue > 0) {
      setFractionAmount(inputValue)
      onValueChange(inputValue)
    } else {
      setFractionAmount(minFractionAmount)
      onValueChange(minFractionAmount)
    }
  }

  const checkMinFraction = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue: number = parseInt(e.target.value)
    if (inputValue < minFractionAmount) {
      setFractionAmount(minFractionAmount)
      onValueChange(minFractionAmount)
    }
  }

  return (
    <div>
      <div className="mb-1 flex items-center text-neutralBlack">
        <div className="text-sm"> {t('fractionInput.fractionQuantity')}</div>
        <div className="ml-1 text-xs font-light">
          (
          {formatCurrency(
            property.fractionCurrentPrice,
            0,
            2,
            currency,
            locale,
          )}
          /{t('fractionInput.each')})
        </div>
      </div>

      <div className="flex w-full items-center">
        <div className="relative mr-1 flex flex-grow">
          <div className="absolute right-5 top-1/2 -translate-y-1/2 text-sm font-light text-neutralBlack">
            {formatCurrency(
              property.fractionCurrentPrice * fractionAmount,
              0,
              2,
              currency,
              locale,
            )}
          </div>
          <input
            type="number"
            inputMode="numeric"
            step={1}
            min={minFractionAmount}
            max={fractionAvailable}
            className="text-input w-full"
            value={fractionAmount}
            onClick={(e) => e.currentTarget.select()}
            onBlur={checkMinFraction}
            onChange={onFractionChange}
            onWheel={(e) => e.currentTarget.blur()} // 🔥 désactive le scroll sur l'input
            style={{ background: 'transparent' }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault()
              }
            }}
          />
        </div>
        <div className="mr-1 flex">
          <Button
            hierarchy={buttonHierarchy.primary}
            shape={buttonShape.rectangle}
            className="w-6"
            onClick={handleIncrement}
          >
            +
          </Button>
        </div>
        <div className="flex">
          <Button
            hierarchy={buttonHierarchy.custom}
            shape={buttonShape.rectangle}
            className="w-6 rounded-lg border border-brandDarkPurple bg-transparent text-brandDarkPurple"
            onClick={handleDecrement}
          >
            -
          </Button>
        </div>
      </div>
    </div>
  )
}
